import { ApiError, apiFetch, del, get, patch, post } from '@/lib/api';
import { toBillable } from './reference';
import {
  num,
  numOrNull,
  rec,
  str,
  strArray,
  strOrNull,
  toDayOrNull,
  unwrapList
} from './normalize';
import type {
  CreditOrFee,
  InvoiceDetail,
  InvoiceEmailLog,
  InvoiceEntry,
  InvoiceStatus,
  InvoiceSummary,
  Paginated,
  PaymentMethod,
  QuickInvoice
} from '@/types';

const STATUSES: InvoiceStatus[] = [
  'tentative',
  'draft',
  'sent',
  'paid',
  'archived',
  'discarded'
];

function toStatus(v: unknown): InvoiceStatus {
  const s = str(v).toLowerCase();
  if (s === 'deleted') return 'discarded';
  return (STATUSES as string[]).includes(s) ? (s as InvoiceStatus) : 'draft';
}

export function toInvoiceSummary(v: unknown): InvoiceSummary {
  const i = rec(v);
  const client = rec(i.client);
  const billable = rec(i.billable);
  return {
    id: str(i.id),
    invoiceNumber: str(i.invoiceNumber),
    clientId: str(i.clientId),
    clientName: strOrNull(i.clientName ?? client.name),
    billableId: strOrNull(i.billableId ?? billable.id),
    billableName: strOrNull(i.billableName ?? billable.name),
    status: toStatus(i.status),
    totalHours: num(i.totalHours),
    // `totalAmount` is the COMPLETE total — the server recomputes it from the
    // live entries and credits/fees (GET /api/invoices, GET /api/invoices/:id),
    // exactly as v1's getInvoices() server action did. `creditsAndFeesTotal` is
    // a sibling breakdown for display; adding it here double-counts.
    // This read `+ num(i.creditsAndFeesTotal)` until the first real
    // server integration: the dev mock always sent creditsAndFeesTotal: '0', so
    // the extra term was a no-op against it and the bug stayed invisible.
    totalAmount: num(i.totalAmount),
    issueDate: toDayOrNull(i.issueDate),
    dueDate: toDayOrNull(i.dueDate),
    paidDate: toDayOrNull(i.paidDate),
    sentDate: toDayOrNull(i.sentDate),
    paymentSubmittedAt: strOrNull(i.paymentSubmittedAt),
    createdAt: str(i.createdAt)
  };
}

export function toInvoiceEntry(v: unknown): InvoiceEntry {
  const e = rec(v);
  return {
    id: str(e.id),
    date: toDayOrNull(e.date),
    clientId: str(e.clientId),
    activityId: str(e.activityId),
    activityName: strOrNull(e.activityName),
    userId: str(e.userId ?? e.employeeId),
    userName: strOrNull(e.userName ?? e.employeeName),
    hours: num(e.hours ?? e.time ?? e.totalHours),
    misc: strOrNull(e.misc),
    hourlyRate: numOrNull(e.hourlyRate)
  };
}

export function toCreditOrFee(v: unknown): CreditOrFee {
  const c = rec(v);
  const amount = num(c.amount);
  return {
    id: str(c.id),
    clientId: strOrNull(c.clientId),
    clientName: strOrNull(c.clientName),
    invoiceId: strOrNull(c.invoiceId),
    // v1 signs the single `amount` column: negative = credit.
    kind: amount < 0 ? 'credit' : 'fee',
    description: str(c.name ?? c.description),
    amount: Math.abs(amount),
    isHourly: c.isHourly === true,
    date: toDayOrNull(c.date),
    applied: Boolean(c.invoiceId),
    invoiceNumber: strOrNull(c.invoiceNumber),
    invoiceStatus: strOrNull(c.invoiceStatus) as InvoiceStatus | null
  };
}

/** Back to v1's signed-amount encoding. */
export function creditFeeAmount(c: Pick<CreditOrFee, 'kind' | 'amount'>) {
  return c.kind === 'credit' ? -Math.abs(c.amount) : Math.abs(c.amount);
}

// ---------------------------------------------------------------------- list

export interface InvoiceListQuery {
  status?: InvoiceStatus | 'all' | 'overdue';
  search?: string;
  page?: number;
  pageSize?: number;
}

/**
 * NOTE FOR apps/server: v1 has NO list endpoint — `/invoices` is rendered by a
 * server action (`getInvoices()` in `lib/db/actions.ts`). The SPA needs
 *   GET /api/invoices?status=&search=&page=&pageSize=
 *     -> { data: Invoice[], pagination: { page, pageSize, totalCount, totalPages } }
 * `status=overdue` is a derived filter (dueDate < today AND status NOT IN
 * (paid, archived, tentative, deleted)), not a stored value.
 */
export async function fetchInvoices(
  query: InvoiceListQuery = {},
  signal?: AbortSignal
): Promise<Paginated<InvoiceSummary>> {
  const raw = rec(
    await get<unknown>(
      '/api/invoices',
      {
        status: query.status && query.status !== 'all' ? query.status : undefined,
        search: query.search || undefined,
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 50
      },
      signal
    )
  );
  const pagination = rec(raw.pagination);
  return {
    items: unwrapList(raw, 'invoices').map(toInvoiceSummary),
    page: num(pagination.page ?? pagination.currentPage ?? raw.page, query.page ?? 1),
    pageSize: num(pagination.pageSize ?? raw.pageSize, query.pageSize ?? 50),
    totalCount: num(pagination.totalCount ?? raw.totalCount),
    totalPages: num(pagination.totalPages ?? raw.totalPages, 1)
  };
}

// -------------------------------------------------------------------- detail

export function toInvoiceDetail(v: unknown): InvoiceDetail {
  const i = rec(v);
  const summary = toInvoiceSummary(i);
  const entries = unwrapList(i.entries).map(toInvoiceEntry);
  const creditsAndFees = unwrapList(i.creditsAndFees).map(toCreditOrFee);
  const hourlyRate = num(i.hourlyRate);

  const subtotal = entries.reduce((sum, e) => sum + e.hours * (e.hourlyRate ?? hourlyRate), 0);
  const creditsTotal = creditsAndFees
    .filter((c) => c.kind === 'credit')
    .reduce((s, c) => s + c.amount, 0);
  const feesTotal = creditsAndFees
    .filter((c) => c.kind === 'fee')
    .reduce((s, c) => s + c.amount, 0);

  const billTo = i.billTo ?? i.billable;

  return {
    ...summary,
    hourlyRate,
    entries,
    creditsAndFees,
    notes: strOrNull(i.notes),
    templateId: strOrNull(i.template ?? i.templateId),
    billTo: billTo ? toBillable(billTo) : null,
    subtotal,
    creditsTotal,
    feesTotal,
    // Recomputed locally so the header total always agrees with the line items,
    // even when the server's stored totalAmount is stale.
    totalAmount: subtotal + feesTotal - creditsTotal,
    lateInterest: toLateInterest(i.lateInterest),
    amountDue: subtotal + feesTotal - creditsTotal + (toLateInterest(i.lateInterest)?.amount ?? 0),
    totalHours: entries.reduce((s, e) => s + e.hours, 0),
    paymentLinkUrl: toPayUrl(i),
    // Written by the Stripe webhook when the client actually pays; absent on
    // an invoice a firm marked paid by hand.
    amountPaidCents: numOrNull(i.amountPaidCents),
    platformFeeCents: numOrNull(i.platformFeeCents),
    paymentMethod: toPaymentMethod(i.paymentMethod),
    periodStart: toDayOrNull(i.periodStart),
    periodEnd: toDayOrNull(i.periodEnd)
  };
}

function toLateInterest(v: unknown): InvoiceDetail['lateInterest'] {
  if (!v || typeof v !== 'object') return null;
  const l = rec(v);
  const amount = num(l.amount);
  return amount > 0
    ? { rate: num(l.rate), period: l.period === 'year' ? 'year' : 'month', amount }
    : null;
}

function toPaymentMethod(v: unknown): PaymentMethod | null {
  return v === 'card' || v === 'ach' ? v : null;
}

/**
 * The pay link the detail endpoint implies.
 *
 * The server stores the unguessable token, not the URL, so the durable field
 * on the row is `publicPayToken` and the page lives at `/pay/<token>` on this
 * same origin. `paymentUrl` is the old Stripe Checkout URL column and is left
 * as a fallback for rows written before the pay page existed.
 */
function toPayUrl(i: Record<string, unknown>): string | null {
  const token = strOrNull(i.publicPayToken);
  if (token && typeof window !== 'undefined') return `${window.location.origin}/pay/${token}`;
  return strOrNull(i.paymentUrl ?? i.paymentLinkUrl);
}

export async function fetchInvoice(id: string, signal?: AbortSignal): Promise<InvoiceDetail> {
  const raw = rec(await get<unknown>(`/api/invoices/${id}`, undefined, signal));
  return toInvoiceDetail(raw.invoice ?? raw.data ?? raw);
}

export async function fetchInvoiceEntries(id: string, signal?: AbortSignal) {
  const raw = await get<unknown>(`/api/invoices/${id}/entries`, undefined, signal);
  return unwrapList(raw, 'entries').map(toInvoiceEntry);
}

// ------------------------------------------------------------------- writes

export interface InvoicePatch {
  invoiceNumber?: string;
  issueDate?: string;
  dueDate?: string;
  hourlyRate?: number;
  billableId?: string | null;
  billableName?: string;
  billableAddress?: string | null;
  billablePhone?: string | null;
  billableEmails?: string[];
  clientName?: string;
  notes?: string | null;
  entries?: { id: string; activityId?: string; date?: string; hours?: number; hourlyRate?: number | null }[];
  /** Line items taken off the invoice. The hours go back to the timesheet, unbilled. */
  removedEntryIds?: string[];
  /** Line items merged into another line. Their hours are on that line; they stay billed. */
  mergedEntryIds?: string[];
  /** Line items deleted outright. They leave the invoice and the timesheet. */
  deletedEntryIds?: string[];
  /** Full replace — anything omitted is deleted server-side. */
  creditsAndFees?: CreditOrFee[];
}

export const updateInvoice = (id: string, input: InvoicePatch) =>
  patch(`/api/invoices/${id}/batch-update`, {
    ...input,
    entries: input.entries?.map((e) => ({
      id: e.id,
      activityId: e.activityId,
      date: e.date,
      time: e.hours,
      hourlyRate: e.hourlyRate
    })),
    creditsAndFees: input.creditsAndFees?.map((c) => ({
      id: c.id.startsWith('tmp_') ? undefined : c.id,
      name: c.description,
      amount: creditFeeAmount(c),
      date: c.date ?? undefined
    }))
  });

export const setInvoiceStatus = (id: string, status: InvoiceStatus) =>
  patch(`/api/invoices/${id}/status`, { status });

export type BankTransferStatus =
  | { state: 'awaiting_verification'; verificationUrl: string; depositArrivesOn: string | null }
  | { state: 'debit_started'; startedOn: string | null; expectedOn: string | null }
  | { state: 'unknown' };
export const fetchBankTransferStatus = (id: string) =>
  get<BankTransferStatus>(`/api/invoices/${id}/bank-transfer-status`);
export const cancelBankTransfer = (id: string) =>
  post<{ ok: true }>(`/api/invoices/${id}/cancel-bank-transfer`);
export const markInvoicePaid = (id: string) => patch(`/api/invoices/${id}/mark-paid`);
export const archiveInvoice = (id: string) => patch(`/api/invoices/${id}/archive`);
export const discardInvoice = (id: string) => del(`/api/invoices/${id}/discard`);
export const deleteInvoice = (id: string) => del(`/api/invoices/${id}/delete`);

export const setInvoiceTemplate = (id: string, template: string) =>
  patch(`/api/invoices/${id}/template`, { template });

/**
 * Mint (or re-read) the invoice's public pay link.
 *
 * The route is `/api/stripe/invoices/:id/payment-link`, NOT
 * `/api/invoices/:id/payment-link` — the latter only ever existed in the dev
 * mock, which is why this looked like it worked and 404'd against the server.
 *
 * The response is a Billable pay-page URL. It is deliberately not a Stripe
 * URL: a Checkout Session is priced for one rail and the client has not picked
 * card or ACH yet, so the session is created later from the pay page. There is
 * no `sessionId`, no `reused` flag and no fee quote here any more.
 */
export const createPaymentLink = (id: string) =>
  post<PaymentLinkResponse>(`/api/stripe/invoices/${id}/payment-link`);

export interface PaymentLinkResponse {
  url: string;
}

export interface CreateInvoiceInput {
  clientId: string;
  billableId: string | null;
  hourlyRate: number;
  entryIds: string[];
  totalHours: number;
  totalAmount: number;
  invoiceNumber?: string;
  issueDate?: string;
  dueDate?: string;
  status?: InvoiceStatus;
  creditsAndFees?: CreditOrFee[];
}

export const createInvoice = (input: CreateInvoiceInput) =>
  post<unknown>('/api/invoices', {
    ...input,
    creditsAndFees: input.creditsAndFees?.map((c) => ({
      id: c.id.startsWith('tmp_') ? undefined : c.id,
      name: c.description,
      amount: creditFeeAmount(c),
      date: c.date ?? undefined
    }))
  }).then(toInvoiceSummary);

/** Attach already-existing time entries to an invoice. */
export const attachEntries = (
  id: string,
  entries: { id: string; hourlyRate?: number; markAsInvoiced?: boolean }[]
) => post(`/api/invoices/${id}/entries`, { entries });

// -------------------------------------------------------------------- excel

export async function downloadInvoiceExcel(id: string, invoiceNumber: string) {
  const res = await apiFetch(`/api/invoices/${id}/excel`);
  if (!res.ok) throw new Error('Could not generate the spreadsheet.');
  const blob = await res.blob();
  triggerDownload(blob, `Invoice_${invoiceNumber}.xlsx`);
}

export function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking synchronously can cancel the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// -------------------------------------------------------------------- email

export interface SendInvoiceInput {
  pdf: Blob;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  html: string;
  billableId: string | null;
  hourlyRate: number;
  totalHours: number;
  totalAmount: number;
}

export async function sendInvoiceWithPdf(id: string, input: SendInvoiceInput) {
  const form = new FormData();
  form.append('pdfFile', input.pdf, `invoice-${id}.pdf`);
  form.append('invoiceId', id);
  form.append('to', input.to.join(','));
  form.append('subject', input.subject);
  form.append('html', input.html);
  if (input.cc?.length) form.append('cc', input.cc.join(','));
  if (input.bcc?.length) form.append('bcc', input.bcc.join(','));
  if (input.billableId) form.append('billableId', input.billableId);
  form.append('hourlyRate', String(input.hourlyRate));
  form.append('totalHours', String(input.totalHours));
  form.append('totalAmount', String(input.totalAmount));

  const res = await fetch(`${import.meta.env.VITE_API_BASE_URL ?? ''}/api/invoices/${id}/send-with-pdf`, {
    method: 'POST',
    credentials: 'include',
    body: form
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? 'Could not send the invoice.');
  }
  return res.json();
}

/**
 * Record a message Billable composed but did not deliver itself.
 *
 * `sentVia` is validated server-side against ['billable', 'mail_client'] and
 * defaults to 'mail_client' on this route, but it is passed explicitly so the
 * row says what it means rather than relying on a default. `sentTo`/`cc`/`bcc`
 * accept an array or a comma string; arrays are clearer from here.
 */
export const logInvoiceEmail = (
  id: string,
  input: {
    subject: string;
    emailBody: string;
    sentTo: string | string[];
    cc?: string[];
    bcc?: string[];
    emailType?: string;
    sentVia?: 'billable' | 'mail_client';
  }
) => post(`/api/invoices/${id}/log-email`, input);

export const sendFollowUp = (id: string) => post(`/api/invoices/${id}/send-follow-up`);

// ------------------------------------------------------- send via Billable

/** What the server would have mailed, handed back when it cannot mail it. */
export interface InvoiceEmailPreview {
  subject: string;
  from: string;
  to: string[];
  cc: string[];
  bcc: string[];
  viewUrl: string;
  payUrl: string | null;
}

export interface SendInvoiceResult {
  success: boolean;
  emailId: string;
  invoiceId: string;
  status: 'sent';
  subject: string;
  sentTo: string[];
  cc: string[];
  bcc: string[];
  viewUrl: string;
  payUrl: string | null;
  resendId: string | null;
}

/**
 * POST /api/invoices/:id/send — Billable renders the invoice, mails it through
 * Resend, logs it and marks it sent. No request body: everything the email
 * needs already lives on the server (see routes/app/invoice-send.ts).
 */
export const sendInvoice = (id: string) =>
  post<SendInvoiceResult>(`/api/invoices/${id}/send`);

/**
 * The app-facing routes answer with a FLAT `{ error: "...", code?: "..." }`,
 * not the `{ error: { code, message } }` envelope `toApiError` unpacks. A
 * string `body.error` has no `.code`/`.message`, and there is no top-level
 * `message` to fall back to, so `ApiError.message` is left as the generic
 * "Request failed (400)" and `.code` as 'http_error'. Both of the readers below
 * therefore go to `.body` rather than trusting those fields.
 */
function errorBody(err: unknown): Record<string, unknown> | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  return body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
}

/** The server's own wording when it wrote one; the caller's fallback otherwise. */
export function invoiceErrorText(err: unknown, fallback: string): string {
  const text = errorBody(err)?.error;
  return typeof text === 'string' && text.trim() ? text : fallback;
}

export function invoiceErrorCode(err: unknown): string | null {
  const code = errorBody(err)?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * A 501 is not really a failure — it is the state every deployment without a
 * RESEND_API_KEY sits in. The server hands back everything it would have sent
 * so the caller can show it instead of an error.
 */
export function emailNotConfigured(
  err: unknown
): { detail: string; preview: InvoiceEmailPreview } | null {
  if (!(err instanceof ApiError) || err.status !== 501) return null;
  const body = errorBody(err);
  if (!body || body.code !== 'EMAIL_NOT_CONFIGURED') return null;
  const p = rec(body.preview);
  return {
    detail: str(body.detail),
    preview: {
      subject: str(p.subject),
      from: str(p.from),
      to: strArray(p.to),
      cc: strArray(p.cc),
      bcc: strArray(p.bcc),
      viewUrl: str(p.viewUrl),
      payUrl: strOrNull(p.payUrl)
    }
  };
}

export function toEmailLog(v: unknown): InvoiceEmailLog {
  const e = rec(v);
  const related = rec(e.relatedInvoice);
  const type = str(e.emailType ?? e.kind);
  return {
    id: str(e.id),
    invoiceId: str(e.relatedInvoiceId ?? e.invoiceId),
    invoiceNumber: strOrNull(related.invoiceNumber),
    to: strArray(e.sentTo ?? e.to),
    cc: strArray(e.cc),
    bcc: strArray(e.bcc),
    subject: str(e.subject),
    body: strOrNull(e.body),
    kind:
      type === 'follow_up'
        ? 'follow_up'
        : type === 'overdue' || type === 'reminder'
          ? 'reminder'
          : type === 'receipt'
          ? 'receipt'
          : 'invoice',
    // Anything the server does not explicitly mark as a handoff is a send
    // Billable performed itself.
    sentVia: e.sentVia === 'mail_client' ? 'mail_client' : 'billable',
    sentAt: str(e.createdAt ?? e.sentAt),
    status: e.status === 'failed' ? 'failed' : 'sent',
    error: strOrNull(e.error)
  };
}

export async function fetchInvoiceEmails(id: string, signal?: AbortSignal) {
  const raw = await get<unknown>(`/api/invoices/${id}/emails`, undefined, signal);
  return unwrapList(raw, 'emails').map(toEmailLog);
}

// ---------------------------------------------------------- quick invoicing

/**
 * Tentative invoices — one auto-generated draft per client with uninvoiced
 * time. The v1 payload is the full `InvoiceData` blob, so this reuses the
 * detail normalizer and layers the quick-invoice-specific summary on top.
 */
export interface TentativeBundle {
  invoices: InvoiceDetail[];
  message: string;
  status: 'success' | 'no-invoices' | 'error';
}

function toTentativeBundle(raw: Record<string, unknown>): TentativeBundle {
  const list = unwrapList(raw, 'invoices', 'tentativeInvoices').map((v) => {
    const detail = toInvoiceDetail(v);
    return { ...detail, status: 'tentative' as const };
  });
  const status = str(raw.status, list.length ? 'success' : 'no-invoices');
  return {
    invoices: list,
    message: str(raw.message),
    status: status === 'no-invoices' || status === 'error' ? status : 'success'
  };
}

export async function fetchTentativeInvoices(signal?: AbortSignal): Promise<TentativeBundle> {
  const raw = rec(await get<unknown>('/api/initial-tentative-invoices', undefined, signal));
  const initial = rec(raw.initialData ?? raw);
  return toTentativeBundle(initial);
}

export async function regenerateTentativeInvoices(): Promise<TentativeBundle> {
  const raw = rec(await post<unknown>('/api/create-tentative-invoices'));
  return toTentativeBundle(raw);
}

export const convertTentativeInvoice = (input: {
  tentativeInvoice: unknown;
  status: InvoiceStatus;
}) =>
  post<{ success: boolean; invoiceId: string; invoiceNumber: string }>(
    '/api/quick-invoice/convert-to-real',
    input
  );

export function toQuickInvoice(detail: InvoiceDetail): QuickInvoice {
  const dates = detail.entries.map((e) => e.date).filter((d): d is string => !!d).sort();
  return {
    id: detail.id,
    clientId: detail.clientId,
    clientName: detail.clientName,
    status: detail.billTo?.emails.length ? 'ready' : 'draft',
    totalHours: detail.totalHours,
    totalAmount: detail.totalAmount,
    periodStart: dates[0] ?? null,
    periodEnd: dates[dates.length - 1] ?? null,
    entryCount: detail.entries.length,
    createdAt: detail.createdAt,
    convertedInvoiceId: null
  };
}
